import { useCallback } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Download, Eye, Loader2, Printer, Send, ShieldCheck, UploadCloud } from "lucide-react";
import { DocumentViewDialog, type DocumentViewAction } from "@/components/documents/DocumentViewDialog";
import { SignedIndicator } from "@/components/documents/SignedIndicator";
import { TermPrintPreview } from "@/components/terms/TermPrintPreview";
import { deriveRequiredSignatories } from "@/lib/terms/signature-requirements";
import type { PatientTerm } from "@/hooks/usePatientTerms";
import { useRenderPdf } from "@/hooks/useRenderPdf";
import { printPdf } from "@/lib/pdf/download";
import type { usePrintSettings } from "@/hooks/usePrintSettings";

type PrintSettings = ReturnType<typeof usePrintSettings>["data"];

export type TermViewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  term: PatientTerm | null;
  renderedContent: { title: string; content: string } | null;
  printSettings: PrintSettings;
  /** Whether this term has already been digitally signed */
  isDigitallySigned: boolean;
  /** Whether the current user has an A1 certificate loaded */
  hasCertificate: boolean;
  /** Whether a signing operation is in flight for this term */
  isSigningTerm: boolean;
  signPdfPending: boolean;
  onSign?: () => void;
  onDownloadSigned?: () => void;
  /** Whether a manual uploaded file exists */
  hasStoredFile: boolean;
  isUploadPending: boolean;
  onViewStoredFile?: () => void;
  /** Called with the selected file for upload */
  onUploadFile?: (file: File) => void;
  canUpload?: boolean;
  /** Called after the browser print dialog completes (e.g. to mark as printed) */
  onPrinted?: () => void;
  isPrintPending?: boolean;
  /** Called when the user clicks "Enviar" — only shown when isDigitallySigned */
  onShare?: () => void;
  /** Called when the user sends the term to the responsável to sign remotely. */
  onSendForSignature?: () => void;
  /** When set, the "Enviar para assinatura" button renders disabled with this tooltip instead of being hidden. */
  sendForSignatureDisabledReason?: string;
};

export const TermViewDialog = ({
  open,
  onOpenChange,
  term,
  renderedContent,
  printSettings,
  isDigitallySigned,
  hasCertificate,
  isSigningTerm,
  signPdfPending,
  onSign,
  onDownloadSigned,
  hasStoredFile,
  isUploadPending,
  onViewStoredFile,
  onUploadFile,
  canUpload = true,
  onPrinted,
  isPrintPending = false,
  onShare,
  onSendForSignature,
  sendForSignatureDisabledReason,
}: TermViewDialogProps) => {
  const { t } = useTranslation("terms");
  const renderPdf = useRenderPdf();
  const termId = term?.id;
  // Unsigned PDF rendered by the PDF Lambda; errors toast inside the hook.
  const handleBrowserPrint = useCallback(async () => {
    if (!termId) return;
    try {
      const bytes = await renderPdf.mutateAsync({ resource_type: "patient_term", resource_id: termId });
      printPdf(bytes);
      onPrinted?.();
    } catch (error) {
      console.error("Erro ao gerar PDF do termo:", error);
      toast.error(t("panel.printFailed"));
    }
  }, [renderPdf, termId, onPrinted, t]);

  if (!term) return null;

  const requiredSignatories = deriveRequiredSignatories(term.template_snapshot_content ?? "");
  // The legacy solo vet-signing path must never be offered when the responsável
  // also has to sign: it would let the vet complete/seal the term unilaterally,
  // bypassing the responsável's mandatory advanced signature entirely. Co-signed
  // terms must go exclusively through "Enviar para assinatura".
  const canSignDigitally =
    hasCertificate &&
    requiredSignatories.includes("vet") &&
    !requiredSignatories.includes("responsible") &&
    !isDigitallySigned &&
    Boolean(renderedContent);

  const fileInputId = `term-upload-${term.id}`;

  const actions: DocumentViewAction[] = [
    ...(!isDigitallySigned && (onSendForSignature || sendForSignatureDisabledReason)
      ? [
          {
            key: "send-for-signature",
            label: t("signature.send.title"),
            icon: Send,
            onClick: () => onSendForSignature?.(),
            disabled: Boolean(sendForSignatureDisabledReason),
            variant: "default" as const,
          },
        ]
      : []),
    ...(isDigitallySigned && onShare
      ? [{ key: "share", label: t("share.send"), icon: Send, onClick: onShare, variant: "default" as const }]
      : []),
    ...(!isDigitallySigned && canSignDigitally
      ? [
          {
            key: "sign",
            label: isSigningTerm || signPdfPending ? "Assinando..." : "Assinar digitalmente",
            icon: isSigningTerm || signPdfPending ? Loader2 : ShieldCheck,
            onClick: () => onSign?.(),
            disabled: isSigningTerm || signPdfPending,
            loading: isSigningTerm || signPdfPending,
            variant: "default" as const,
          },
        ]
      : []),
    {
      key: "print",
      label: renderPdf.isPending
        ? t("panel.status.preparing")
        : isPrintPending
          ? "Registrando..."
          : "Imprimir termo",
      icon: renderPdf.isPending || isPrintPending ? Loader2 : Printer,
      onClick: () => void handleBrowserPrint(),
      disabled: !renderedContent || isPrintPending || renderPdf.isPending,
      loading: renderPdf.isPending || isPrintPending,
      variant: "outline" as const,
    },
    ...(isDigitallySigned
      ? [
          {
            key: "download-signed",
            label: "Baixar PDF assinado",
            icon: Download,
            onClick: () => onDownloadSigned?.(),
            secondary: true,
          },
        ]
      : []),
    ...(!isDigitallySigned
      ? hasStoredFile
        ? [
            {
              key: "view-stored-file",
              label: "Ver arquivo armazenado",
              icon: Eye,
              onClick: () => onViewStoredFile?.(),
              secondary: true,
            },
          ]
        : [
            {
              key: "upload-file",
              label: isUploadPending ? "Enviando..." : "Armazenar arquivo",
              icon: UploadCloud,
              onClick: () => document.getElementById(fileInputId)?.click(),
              disabled: !canUpload || isUploadPending,
              secondary: true,
            },
          ]
      : []),
  ];

  return (
    <>
      <DocumentViewDialog
        open={open}
        onOpenChange={onOpenChange}
        variant="fullscreen"
        title="Detalhes do Termo"
        statusBadge={isDigitallySigned ? <SignedIndicator /> : undefined}
        subtitle={term.title}
        actions={actions}
        moreActionsLabel={t("viewDialog.moreActions", { defaultValue: "Mais ações" })}
      >
        {renderedContent ? (
          <TermPrintPreview
            title={renderedContent.title}
            content={renderedContent.content}
            printSettings={printSettings}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            Este termo não possui modelo vinculado para pré-visualização de impressão.
          </p>
        )}
      </DocumentViewDialog>
      {open && !isDigitallySigned && !hasStoredFile ? (
        <input
          id={fileInputId}
          type="file"
          className="hidden"
          accept="application/pdf,image/*"
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            if (file) onUploadFile?.(file);
            event.currentTarget.value = "";
          }}
        />
      ) : null}
    </>
  );
};
